import { useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import {
  listWebhooks,
  createWebhook,
  deleteWebhook,
  Webhook,
  CreatedWebhook,
} from '../services/webhooks';
import { friendlyError } from '../utils/errors';
import { trackEvent } from '../firebase';
import { HelpDrawer } from './ui/HelpDrawer';

interface WebhookManagerProps {
  user: User;
  projectId: string;
}

const NODE_VERIFY_SNIPPET = (secret: string) => `const crypto = require('crypto');

app.post('/webhook',
  express.raw({ type: 'application/json' }),
  (req, res) => {
    const sig = req.headers['x-plw-signature'];
    const expected =
      'sha256=' +
      crypto
        .createHmac('sha256', '${secret || 'YOUR_WEBHOOK_SECRET'}')
        .update(req.body)
        .digest('hex');

    if (sig !== expected) {
      return res.status(401).send('Invalid signature');
    }

    const event = JSON.parse(req.body);
    if (event.event === 'subscriber.created') {
      console.log('New subscriber:', event.data.email);
    }

    res.sendStatus(200);
  }
);`;

const PAYLOAD_EXAMPLE = `{
  "event": "subscriber.created",
  "projectId": "your-project-id",
  "timestamp": "2026-04-10T10:30:00.000Z",
  "data": {
    "email": "user@example.com",
    "subscribedAt": "2026-04-10T10:30:00.000Z",
    "metadata": {}
  }
}`;

function WebhookHelp({ secret }: { secret: string }) {
  const [tab, setTab] = useState<'simple' | 'advanced'>('simple');

  return (
    <div>
      {/* Tab switcher */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1">
        <button
          type="button"
          onClick={() => setTab('simple')}
          className={`flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors ${
            tab === 'simple'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Make.com / n8n
        </button>
        <button
          type="button"
          onClick={() => setTab('advanced')}
          className={`flex-1 text-xs font-semibold py-1.5 rounded-md transition-colors ${
            tab === 'advanced'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Custom server (Node.js)
        </button>
      </div>

      {tab === 'simple' && (
        <div>
          <h3 className="text-sm font-bold text-gray-800 mb-1">Make.com</h3>
          <p className="text-sm text-gray-600 mb-3">
            Make doesn&apos;t support HMAC verification natively — skip it and use a{' '}
            <strong>Custom webhook</strong> trigger instead. Keep your URL private; that&apos;s your
            security boundary.
          </p>
          <ol className="text-sm text-gray-600 space-y-2 pl-4 list-decimal mb-5">
            <li>
              Create a new scenario and add a <strong>Webhooks → Custom webhook</strong> trigger.
            </li>
            <li>Copy the generated URL and paste it into the webhook field.</li>
            <li>
              Add a <strong>Filter</strong> step: check that{' '}
              <code className="bg-gray-100 px-1 rounded">x-plw-event</code> equals{' '}
              <code className="bg-gray-100 px-1 rounded">subscriber.created</code>.
            </li>
            <li>
              Map <code className="bg-gray-100 px-1 rounded">data.email</code> from the JSON body to
              whatever action you want.
            </li>
          </ol>

          <h3 className="text-sm font-bold text-gray-800 mb-1">n8n</h3>
          <p className="text-sm text-gray-600 mb-3">
            Same approach — use the <strong>Webhook</strong> trigger node.
          </p>
          <ol className="text-sm text-gray-600 space-y-2 pl-4 list-decimal mb-5">
            <li>
              Add a <strong>Webhook</strong> node, set Method to <strong>POST</strong>.
            </li>
            <li>Copy the production URL and paste it above.</li>
            <li>
              Add an <strong>If</strong> node: check header{' '}
              <code className="bg-gray-100 px-1 rounded">x-plw-event</code> ={' '}
              <code className="bg-gray-100 px-1 rounded">subscriber.created</code>.
            </li>
            <li>
              Map <code className="bg-gray-100 px-1 rounded">body.data.email</code> to your next
              step.
            </li>
          </ol>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <strong>Security note:</strong> without signature verification, anyone with your webhook
            URL can send fake events. Keep it secret and rotate it if you suspect exposure.
          </div>
        </div>
      )}

      {tab === 'advanced' && (
        <div>
          <h3 className="text-sm font-bold text-gray-800 mb-1">Verify signatures (Node.js)</h3>
          <p className="text-sm text-gray-600 mb-3">
            Every request includes an{' '}
            <code className="bg-gray-100 px-1 rounded">X-PLW-Signature</code> header with a{' '}
            <code className="bg-gray-100 px-1 rounded">sha256=&lt;hex&gt;</code> HMAC digest of the
            raw body, signed with your webhook secret. Use{' '}
            <code className="bg-gray-100 px-1 rounded">express.raw()</code> to preserve the exact
            bytes before JSON parsing.
          </p>
          <pre className="bg-slate-800 text-slate-200 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all mb-5">
            {NODE_VERIFY_SNIPPET(secret)}
          </pre>
          <ul className="text-sm text-gray-600 space-y-1.5 pl-4 list-disc">
            <li>Store the signing secret in an environment variable, never in source code.</li>
            <li>
              Respond with <code className="bg-gray-100 px-1 rounded">2xx</code> within 10 seconds —
              failed deliveries are not retried.
            </li>
            <li>Make your handler idempotent; the same event can arrive more than once.</li>
            <li>You can register multiple webhooks for the same project.</li>
          </ul>
        </div>
      )}

      <hr className="my-5 border-gray-200" />

      <h3 className="text-sm font-bold text-gray-800 mb-1">Event payload</h3>
      <p className="text-sm text-gray-600 mb-2">
        Sent as <code className="bg-gray-100 px-1 rounded">application/json</code> via HTTP POST.
      </p>
      <pre className="bg-slate-800 text-slate-200 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all">
        {PAYLOAD_EXAMPLE}
      </pre>
    </div>
  );
}

export function WebhookManager({ user, projectId }: WebhookManagerProps) {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [urlInput, setUrlInput] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [newWebhook, setNewWebhook] = useState<CreatedWebhook | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  // null = undetermined (while loading), resolves to true when webhooks exist
  const [collapsed, setCollapsed] = useState<boolean | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listWebhooks(user, projectId)
      .then((list) => {
        setWebhooks(list);
        setCollapsed(list.length > 0);
      })
      .catch((err: unknown) => {
        setError(friendlyError(err));
        setCollapsed(false);
      })
      .finally(() => setLoading(false));
  }, [user, projectId]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    setNewWebhook(null);
    if (!urlInput.trim()) {
      setCreateError('Please enter a webhook URL.');
      return;
    }
    setCreating(true);
    try {
      const created = await createWebhook(user, projectId, urlInput.trim());
      trackEvent('create_webhook', { project_id: projectId });
      setNewWebhook(created);
      setWebhooks((prev) => [created, ...prev]);
      setUrlInput('');
      // Expand so the user sees the secret
      setCollapsed(false);
    } catch (err: unknown) {
      setCreateError(friendlyError(err));
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(webhookId: string) {
    if (!confirm('Delete this webhook? This cannot be undone.')) return;
    setDeletingId(webhookId);
    try {
      await deleteWebhook(user, projectId, webhookId);
      trackEvent('delete_webhook', { project_id: projectId });
      const remaining = webhooks.filter((w) => w.id !== webhookId);
      setWebhooks(remaining);
      if (newWebhook?.id === webhookId) setNewWebhook(null);
      if (remaining.length === 0) setCollapsed(false);
    } catch (err: unknown) {
      setError(friendlyError(err));
    } finally {
      setDeletingId(null);
    }
  }

  function copySecret(secret: string) {
    navigator.clipboard.writeText(secret).then(() => {
      trackEvent('copy_webhook_secret', { project_id: projectId });
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    });
  }

  const isCollapsed = collapsed === true;
  const activeCount = webhooks.filter((w) => w.active).length;
  const firstUrl = webhooks[0]?.url ?? '';
  const truncatedUrl = firstUrl.length > 42 ? firstUrl.slice(0, 42) + '…' : firstUrl;

  return (
    <>
      <section className="bg-white rounded-xl p-6 mb-6 shadow-sm">
        {/* Section header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <h3 className="text-base font-bold m-0">Webhooks</h3>
            {isCollapsed && webhooks.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                  ✓ {activeCount} active
                </span>
                <span className="text-xs text-gray-400 hidden sm:inline">{truncatedUrl}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                setHelpOpen(true);
                trackEvent('open_webhook_help', { project_id: projectId });
              }}
              className="bg-transparent border border-gray-300 cursor-pointer text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-brand-500 hover:border-brand-400 transition-colors"
              aria-label="Open webhook help"
            >
              ?
            </button>
            {collapsed !== null && (
              <button
                onClick={() => setCollapsed(!isCollapsed)}
                className="bg-transparent border-0 cursor-pointer text-xl px-2 text-gray-500 hover:text-gray-700"
                aria-label={isCollapsed ? 'Expand webhooks' : 'Collapse webhooks'}
              >
                {isCollapsed ? '▶' : '▼'}
              </button>
            )}
          </div>
        </div>

        {!isCollapsed && collapsed !== null && (
          <>
            <p className="text-sm text-gray-500 mb-3">
              Get an HTTP POST every time someone joins — works with Make, n8n, or any custom
              handler.
            </p>

            {/* Create form */}
            <form onSubmit={handleCreate} className="flex gap-2 mb-3 flex-wrap">
              <input
                type="url"
                className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:border-brand-500 transition-colors"
                placeholder="https://your-server.com/webhook"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                disabled={creating}
              />
              <button
                type="submit"
                className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white border-0 rounded-md text-sm font-semibold whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={creating}
              >
                {creating ? 'Adding…' : 'Add webhook'}
              </button>
            </form>
            {createError && <p className="text-red-500 text-sm mb-2">{createError}</p>}

            {/* Secret reveal (one-time) */}
            {newWebhook && (
              <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-green-800 mb-2">
                  ✅ Webhook created — save your signing secret now. It won&apos;t be shown again.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="flex-1 min-w-0 text-xs bg-green-100 text-green-900 px-2 py-1 rounded break-all">
                    {newWebhook.secret}
                  </code>
                  <button
                    className="px-3 py-1 text-xs font-semibold border border-green-600 rounded-md bg-transparent text-green-600 hover:bg-green-600 hover:text-white transition-colors whitespace-nowrap"
                    onClick={() => copySecret(newWebhook.secret)}
                    type="button"
                  >
                    {secretCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            )}

            {/* Webhook list */}
            {loading && <p className="text-gray-500 text-sm">Loading…</p>}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2 text-sm text-red-700">
                <strong>Couldn&apos;t load webhooks.</strong> {error}
              </div>
            )}
            {!loading && webhooks.length === 0 && (
              <p className="text-gray-500 text-sm">No webhooks configured yet.</p>
            )}
            {webhooks.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left px-3 py-2 border-b-2 border-gray-200 text-gray-700 font-semibold">
                        URL
                      </th>
                      <th className="text-left px-3 py-2 border-b-2 border-gray-200 text-gray-700 font-semibold">
                        Events
                      </th>
                      <th className="text-left px-3 py-2 border-b-2 border-gray-200 text-gray-700 font-semibold">
                        Added
                      </th>
                      <th className="px-3 py-2 border-b-2 border-gray-200"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {webhooks.map((w) => (
                      <tr key={w.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-600 break-all max-w-[300px]">
                          {w.url}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          {w.events.map((ev) => (
                            <span
                              key={ev}
                              className="inline-block text-xs bg-violet-100 text-violet-700 rounded px-1.5 py-0.5 font-semibold mr-1"
                            >
                              {ev}
                            </span>
                          ))}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100 text-gray-600">
                          {w.createdAt ? new Date(w.createdAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-3 py-2 border-b border-gray-100">
                          <button
                            className="px-2 py-1 text-xs font-semibold border border-red-400 rounded-md bg-transparent text-red-500 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleDelete(w.id)}
                            disabled={deletingId === w.id}
                            type="button"
                          >
                            {deletingId === w.id ? '…' : 'Delete'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} title="How to use webhooks">
        <WebhookHelp secret={newWebhook?.secret ?? ''} />
      </HelpDrawer>
    </>
  );
}
